import { useCallback, useEffect, useState } from "react";
import { api } from "../../lib/api";
import type { Member, Tier, UserStatus } from "../../lib/types";
import CustomerDetail from "./CustomerDetail";

const STATUS_TABS: Array<{ k: UserStatus | "all"; l: string }> = [
  { k: "pending", l: "승인 대기" },
  { k: "approved", l: "승인됨" },
  { k: "rejected", l: "반려" },
  { k: "all", l: "전체" },
];

export default function AdminMembers() {
  const [tab, setTab] = useState<UserStatus | "all">("pending");
  const [members, setMembers] = useState<Member[]>([]);
  const [loading, setLoading] = useState(true);
  const [detailId, setDetailId] = useState<string | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    const q = tab === "all" ? "" : `?status=${tab}`;
    api.get<{ items: Member[] }>(`/admin/members${q}`)
      .then((r) => setMembers(r.items))
      .finally(() => setLoading(false));
  }, [tab]);

  useEffect(() => { load(); }, [load]);

  const [busyId, setBusyId] = useState<string | null>(null);

  const approve = async (id: string, tier: Tier) => {
    const m = members.find((x) => x.id === id);
    if (!window.confirm(`${m?.company_name ?? ""} (${m?.business_no ?? ""})을(를) '${tier}' 등급으로 승인하시겠습니까?`)) return;
    setBusyId(id);
    try {
      await api.post(`/admin/members/${id}/approve`, { tier });
      load();
    } finally { setBusyId(null); }
  };
  const reject = async (id: string) => {
    const m = members.find((x) => x.id === id);
    if (!window.confirm(`${m?.company_name ?? ""}을(를) 반려하시겠습니까? 이 작업은 되돌릴 수 없습니다.`)) return;
    const note = prompt("반려 사유를 입력해 주세요 (선택):") ?? "";
    setBusyId(id);
    try {
      await api.post(`/admin/members/${id}/reject`, { note });
      load();
    } finally { setBusyId(null); }
  };

  return (
    <>
      <div className="row" style={{ marginBottom: 16 }}>
        {STATUS_TABS.map((t) => (
          <button key={t.k} className={`btn btn-${tab === t.k ? "primary" : "outline"} btn-sm`} onClick={() => setTab(t.k)}>
            {t.l}
          </button>
        ))}
      </div>

      <div className="card">
        <table>
          <thead><tr>
            <th>업체명</th><th>사업자번호</th><th>대표/연락처</th><th>이메일</th><th>상태</th><th>등급</th><th style={{ textAlign: "right" }}>작업</th>
          </tr></thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} style={{ textAlign: "center", color: "var(--text-muted)", padding: 40 }}>로딩 중...</td></tr>
            ) : members.length === 0 ? (
              <tr><td colSpan={7} style={{ textAlign: "center", color: "var(--text-muted)", padding: 40 }}>해당 상태의 회원이 없습니다.</td></tr>
            ) : (
              members.map((m) => (
                <tr key={m.id}>
                  <td style={{ fontWeight: 600 }}>{m.company_name}</td>
                  <td style={{ fontFamily: "monospace", fontSize: 15 }}>{m.business_no}</td>
                  <td>{m.representative}<div className="muted" style={{ fontSize: 15 }}>{m.phone}</div></td>
                  <td style={{ fontSize: 15 }}>{m.email}</td>
                  <td>
                    <span className={`badge ${m.status === "approved" ? "badge-success" : m.status === "pending" ? "badge-warning" : m.status === "rejected" ? "badge-danger" : "badge-muted"}`}>
                      {m.status}
                    </span>
                  </td>
                  <td style={{ fontSize: 15 }}>{m.tier}</td>
                  <td style={{ textAlign: "right" }}>
                    {m.status === "pending" ? (
                      <div className="row" style={{ justifyContent: "flex-end", gap: 4 }}>
                        <select defaultValue="standard" id={`tier-${m.id}`} style={{ width: "auto", padding: "4px 8px", fontSize: 15 }}>
                          <option value="standard">standard</option>
                          <option value="silver">silver</option>
                          <option value="gold">gold</option>
                        </select>
                        <button className="btn btn-primary btn-sm" disabled={busyId === m.id} onClick={() => {
                          const sel = document.getElementById(`tier-${m.id}`) as unknown as HTMLSelectElement;
                          approve(m.id, sel.value as Tier);
                        }}>{busyId === m.id ? "처리 중..." : "승인"}</button>
                        <button className="btn btn-outline btn-sm" disabled={busyId === m.id} onClick={() => reject(m.id)}>반려</button>
                      </div>
                    ) : (
                      <div className="row" style={{ justifyContent: "flex-end", gap: 4 }}>
                        <button className="btn btn-ghost btn-sm" onClick={() => setDetailId(m.id)}>상세</button>
                      </div>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {detailId && <CustomerDetail userId={detailId} onClose={() => { setDetailId(null); load(); }} />}
    </>
  );
}
